import { IFile } from "@/features/storage/types"
import { zipFiles } from "@/features/storage/utils/api"
import { fetcher, getToken } from "../index"
import { StatusCode } from "@/core/generated/api/enums/status_code"
import contentDisposition from "content-disposition"
import {
  COOKIE_AUTH_ENABLED,
  getAuthFetchOptions,
} from "@/core/generated/api/auth_cookies"

const API_URL = process.env.NEXT_PUBLIC_API_URL

export const listFiles = async (path: string) => {
  return await fetcher(`/storage?path=${path}`)
}

export const addAttachmentsToNode = async (
  attachments: string[],
  node: string,
  fieldKey?: string,
): Promise<StatusCode> => {
  const res: { statusCode: StatusCode; message?: string } = await fetcher(
    `/storage/attachments/attach/${node}`,
    {
      method: "POST",
      body: JSON.stringify({ attachments, fieldKey }),
    },
  )

  return res?.statusCode ?? StatusCode.InternalServerError
}

const downloadFileWithCookieAuth = async (uid: string) => {
  const url = `${API_URL}/storage/files/${uid}/download`

  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", "")
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
const downloadFileWithHeaderAuth = async (uid: string) => {
  const response = await fetch(
    `${API_URL}/storage/files/${uid}/download`,
    getAuthFetchOptions(getToken()),
  )

  if (!response.ok) {
    const body = await response.text().catch(() => "")
    throw new Error(`Failed to download file: ${response.status} ${body}`)
  }

  const contentDispositionHeader = response.headers.get("content-disposition")

  const filename = contentDispositionHeader
    ? (contentDisposition.parse(contentDispositionHeader).parameters.filename ??
      "download")
    : "download"

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)

  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}

/**
 * This utility function behaves a bit differently depending on whether cookie auth is enabled.
 * - Cookie auth: The file download is handled by the browser.
 *   - The download progress can be tracked by the browser, and appears to be begin almost immediately.
 * - Header auth: The file data is downloaded with the Fetch API, and the data is then sent to be downloaded by the browser.
 *   - The download progress cannot be tracked by the browser, and appears when the download is complete.
 */
export const downloadFile = COOKIE_AUTH_ENABLED
  ? downloadFileWithCookieAuth
  : downloadFileWithHeaderAuth

/**
 * @returns true on success. Rejects on error.
 */
export const downloadFiles = async (files: IFile[] | string[]) => {
  try {
    let uids: string[] = []
    if (typeof files?.[0] === "string") {
      uids = files as string[]
    } else {
      uids = (files as IFile[]).map((f) => f.uid)
    }

    if (!uids?.length) {
      throw new Error("No files to download")
    }

    const res = await zipFiles([], uids)
    if (!res?.base64) {
      throw new Error("Failed to download files")
    }

    const blob = await fetch(`data:application/zip;base64,${res.base64}`).then(
      (res) => res.blob(),
    )
    const url = window.URL.createObjectURL(blob)
    window.location.href = url
    setTimeout(() => window.URL.revokeObjectURL(url), 0)
    return true
  } catch (err) {
    return Promise.reject(err)
  }
}
